<template>
    <div class="min-h-screen bg-gray-50">
        <!-- Breadcrumbs -->
        <nav class="flex items-center space-x-2 text-md text-gray-600 py-7 px-14 border-b border-gray-200" aria-label="Breadcrumb">
            <a href="#" @click="goBack()"  class="hover:text-blue-600 transition-colors">Accueil</a>
            <span class="text-gray-400">/</span>
            <span class="font-medium text-gray-900">Ressources</span>
        </nav>
        <!-- Section titre -->
        <section class="text-center mb-16 bg-gradient-to-r from-[#ff7e41] to-[#2A3992] py-16  shadow-lg">
            <h2 class="text-4xl md:text-5xl font-extrabold text-white mb-6">
                Ressources académiques
            </h2>
            <p class="text-lg md:text-xl text-gray-100 max-w-2xl mx-auto leading-relaxed">
                Explorez nos vidéos, audios, articles et événements académiques
                soigneusement sélectionnés pour enrichir vos recherches et soutenir votre apprentissage.
            </p>
            <div class="mt-6 flex justify-center">
                <span class="inline-block px-4 py-2 text-sm font-medium bg-white text-[#2A3992] rounded-full shadow-md">
                  + de 500 ressources disponibles
                </span>
            </div>
        </section>
        <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <!-- Liste des ressources -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ResourceCard
                    v-for="resource in filteredResources"
                    :key="resource.id"
                    :resource="resource"
                />
            </div>
            <div v-if="filteredResources.length === 0" class="text-center py-16">
                <div class="relative">
                    <div class="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full w-24 h-24 mx-auto opacity-50 blur-xl"></div>
                    <svg class="relative mx-auto h-24 w-24 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <h3 class="mt-6 text-2xl font-semibold text-gray-700">Aucune ressource pour l'instant</h3>
                <p class="mt-3 text-gray-500 max-w-md mx-auto">
                    Vous pouvez les consulter dès qu'ils seront mises à jours
                </p>
            </div>
        </main>
    </div>
</template>

<script setup>
import {ref, computed} from 'vue'
import ResourceCard from './ResourceCard.vue'


const searchQuery = ref('')
const selectedTab = ref('all')
const sortBy = ref('date')
const selectedDiscipline = ref('')

const goBack=()=>{
    window.history.back()
}
const resources = ref([
    {
        id: 1,
        type: 'video',
        title: 'Cérémonie de bienvenue',
        description: 'Découvrez les principaux algorithmes de tri avec des exemples pratiques et une analyse de leur complexité temporelle et spatiale.',
        tags: ['Algorithmes', 'Informatique', 'Débutant'],
        image: '📹',
        date: '2024-03-15',
        popularity: 95,
        discipline: 'informatique'
    },
    {
        id: 2,
        type: 'Video',
        title: 'TD Cours de comptabilité',
        description: 'Un guide complet sur le calcul différentiel avec exercices corrigés et applications en physique et ingénierie.',
        tags: ['Finance', 'Avancé'],
        image: '📄',
        date: '2024-03-10',
        popularity: 78,
        discipline: 'Comptabilité'
    },
    {
        id: 3,
        type: 'audio',
        title: 'Podcast: Histoire de la Renaissance',
        description: 'Une série de podcasts explorant les aspects culturels, artistiques et scientifiques de la Renaissance européenne.',
        tags: ['Histoire', 'Culture', 'Renaissance'],
        image: '🎧',
        date: '2024-03-12',
        popularity: 82,
        discipline: 'histoire'
    },
    {
        id: 4,
        type: 'article',
        title: 'Les lois de la thermodynamique',
        description: 'Article détaillé sur les quatre lois fondamentales de la thermodynamique avec des exemples concrets et des applications pratiques.',
        tags: ['Thermodynamique', 'Physique', 'Théorie'],
        image: '📝',
        date: '2024-03-08',
        popularity: 91,
        discipline: 'physique'
    },
    {
        id: 5,
        type: 'event',
        title: 'Conférence: Gestion Admnistrative',
        description: 'Conférence interactive sur les enjeux éthiques de l\'intelligence artificielle avec des experts du domaine.',
        tags: ['IA', 'Éthique', 'Conférence'],
        image: '🎯',
        date: '2024-03-20',
        popularity: 88,
        discipline: 'Administration'
    },
    {
        id: 6,
        type: 'video',
        title: 'Stratégie financière',
        description: 'Cours vidéo sur les mécanismes cellulaires complexes: mitose, méiose, et régulation génique.',
        tags: ['Finance', 'Trésor', 'Avancé'],
        image: '📹',
        date: '2024-03-18',
        popularity: 76,
        discipline: 'biologie'
    },
    {
        id: 7,
        type: 'pdf',
        title: 'Analyse littéraire moderne',
        description: 'Méthodes d\'analyse littéraire contemporaine avec des études de cas sur des œuvres du XXe siècle.',
        tags: ['Littérature', 'Analyse', 'Moderne'],
        image: '📄',
        date: '2024-03-14',
        popularity: 69,
        discipline: 'litterature'
    },
    {
        id: 8,
        type: 'article',
        title: 'Programmation fonctionnelle en JavaScript',
        description: 'Introduction aux concepts de programmation fonctionnelle appliqués au JavaScript moderne avec des exemples pratiques.',
        tags: ['JavaScript', 'Programmation', 'Fonctionnel'],
        image: '📝',
        date: '2024-03-16',
        popularity: 93,
        discipline: 'informatique'
    }
])


const filteredResources = computed(() => {
    let filtered = resources.value

    // Filtre par recherche textuelle
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(resource =>
            resource.title.toLowerCase().includes(query) ||
            resource.description.toLowerCase().includes(query) ||
            resource.tags.some(tag => tag.toLowerCase().includes(query))
        )
    }

    // Filtre par type de ressource
    if (selectedTab.value !== 'all') {
        filtered = filtered.filter(resource => resource.type === selectedTab.value)
    }

    // Filtre par discipline
    if (selectedDiscipline.value) {
        filtered = filtered.filter(resource => resource.discipline === selectedDiscipline.value)
    }

    // Tri
    if (sortBy.value === 'date') {
        filtered.sort((a, b) => new Date(b.date) - new Date(a.date))
    } else if (sortBy.value === 'popularity') {
        filtered.sort((a, b) => b.popularity - a.popularity)
    } else if (sortBy.value === 'title') {
        filtered.sort((a, b) => a.title.localeCompare(b.title))
    }

    return filtered
})
</script>
